import React, { useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { AppState, DashboardData, MainAppReducer } from 'Types';
import * as appActions from 'Actions/app';
import { ContributionHeatmap } from './components/ContributionHeatmap';
import { HorizontalBarChart } from './components/HorizontalBarChart';
import { MinutesPlayedChart } from './components/MinutesPlayedChart';
import { useAuthWrapperContext } from 'Contexts/AuthWrapperContext';
import { CustomCircularProgress } from 'Components/StyledComponents';
import './dashboard.css';
import { Box, Container } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { Menu } from 'Components/RoadmapComponents/Menu';
import { StatsCard } from './components/StatsCard';
import { LevelsCompletionChart } from './components/LevelsCompletionChart';
import { LevelProgressChart } from './components/LevelProgressChart';
import { RepertoireProgressChart } from './components/RepertoireProgressChart';

const Dashboard = () => {
  const auth = useAuthWrapperContext()
  const dispatch = useDispatch();
  const dashboardCacheInvalidated = useSelector((state: AppState) => state.dashboardCacheInvalidated);
  // const dashboardData = useSelector((state: AppState) => state.dashboardData);
  const entireState = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const { dashboardData } = useSelector((state: MainAppReducer) =>  state.mainAppReducer);
  
  console.log("Dashboard component render - entire Redux state:", entireState);
  console.log("dashboardData", dashboardData);
  console.log("Dashboard component render - dashboardData from selector:", dashboardData);
  
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);

  const fetchData = useCallback(async () => {
    console.log("fetchData called");
    console.log("Current dashboardData:", dashboardData);
    
    try {
      setLoading(true);
      setError(null);
      const authToken = await auth?.currentAuthToken();
      console.log("Auth token:", authToken ? "exists" : "missing");
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/v1/metrics/dashboard`, {
        headers: {
          "Access-Control-Allow-Origin": "*",
          "Authorization": `Bearer ${authToken}`
        },
        timeout: 5000 // 5 second timeout
      });
      if (response.status !== 200) {
        throw new Error('Network response was not ok');
      }
      console.log("API response received:", response.data)
      console.log("Dispatching setDashboardData with:", response.data.data)
      dispatch(appActions.setDashboardData(response.data.data));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('There was a problem loading the dashboard. Please try reloading the page.');
    } finally {
      setLoading(false);
    }
  }, [auth, dispatch]); // Remove dashboardData dependency temporarily

  useEffect(() => {
    console.log("Dashboard component mounted/updated");
    console.log("dashboardData:", dashboardData);
    console.log("dashboardCacheInvalidated:", dashboardCacheInvalidated);
    console.log("loading:", loading);
    
    // Only fetch if we don't have data
    if (!dashboardData) {
      console.log("No dashboard data, calling fetchData");
      fetchData();
    } else {
      console.log("Dashboard data exists, setting loading to false");
      setLoading(false);
    }
  }, []); // Remove fetchData dependency to avoid infinite loops

  useEffect(() => {
    console.log("dashboardData changed:", dashboardData);
    if (dashboardData) {
      console.log("Setting loading to false because data exists");
      setLoading(false);
    }
  }, [dashboardData]);

  if(loading) {
    return (
      <Box 
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}
      >
        <Menu selected={"Dashboard"}/>
        <Box sx={{ 
          flex: 1,
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: '#111827',
          color: 'white'
        }}>
          <div className="text-white text-xl">Loading dashboard...</div>
          <CustomCircularProgress />
        </Box>
      </Box>
    );
  }

  if(error) {
    return (
      <Box 
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}
      >
        <Menu selected={"Dashboard"}/>
        <Box sx={{ 
          flex: 1,
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: '#111827',
          color: 'white',
          textAlign: 'center',
          padding: 3
        }}>
          <div className="text-white text-xl mb-4">{error}</div>
          <button 
            onClick={() => fetchData()} 
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            Try Again
          </button>
        </Box>
      </Box>
    );
  }
  
  if (!dashboardData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CustomCircularProgress />
      </Box>
    );
  }

  console.log("Rendering dashboard with data:", dashboardData) 
  return (
    <Box
      sx={{
          height: '100vh',
          overflowY: 'auto',
      }}
    >
      <Menu selected={"Dashboard"}/>
      <Box sx={{ 
        minHeight: '100vh', 
        backgroundColor: '#111827', 
        // paddingLeft: '100px',
        // paddingRight: '100px',
        // paddingTop: '20px',
        display: 'flex',
        // Responsive padding
        paddingLeft: { xs: '116px', sm: '116px', md: '120px', lg: '120px', xl: '120px' },
        paddingRight: { xs: 1, sm: 2, md: 3, lg: 5, xl: 6 },
        paddingTop: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 },
        paddingBottom: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 },
        // overflowX: 'hidden', // Prevent horizontal scrolling
        // overflowY: 'auto',   // Allow vertical scrolling only
      }}>
        <CssBaseline />
        <Container maxWidth={false} 
          sx={{ 
            maxWidth: { xs: '100%', sm: '100%', md: '90rem', lg: '105rem', xl: '120rem' },
            alignContent: 'center',
            justifyContent: 'center', 
          }}
        >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3, lg: 4 } }}>
          {/* Top Section - 3x2 Grid */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '1fr 1fr 1fr' },
            gap: { xs: 2, sm: 3, lg: 4 },
            alignItems: 'stretch'
          }}>
            <Box sx={{ display: 'flex' }}>
              <HorizontalBarChart data={dashboardData.recentLevelAverages} />
            </Box>
            <Box sx={{ display: 'flex' }}>
              <MinutesPlayedChart 
                totalMinutes={dashboardData.totalMinutesPlayed}
                weeklyMinutes={dashboardData.weeklyMinutes}
              />
            </Box>
            <Box sx={{ display: 'flex' }}>
              <LevelsCompletionChart 
                levelsCompleted={dashboardData.levelsCompleted}
                totalLevels={dashboardData.totalLevels}
              />
            </Box>
            <Box sx={{ display: 'flex' }}>
              <RepertoireProgressChart data={dashboardData.runningRepertoireCompleted} />
            </Box>
            <Box sx={{ display: 'flex' }}>
              <LevelProgressChart data={dashboardData.runningHighestLevel} />
            </Box>
            <Box sx={{ display: 'flex' }}>
              <StatsCard 
                totalActiveDays={dashboardData.totalActiveDays}
                longestStreakDays={dashboardData.longestStreakDays}
                currentStreakDays={dashboardData.currentStreakDays}
              />
            </Box>
          </Box>
          
          {/* Bottom Section - Play Sessions Heatmap */}
          <Box sx={{ 
            width: '100%',
            minWidth: 0,
            overflow: 'hidden'
          }}>
            <ContributionHeatmap 
              weeklyIntensity={dashboardData.weeklyIntensity}
              totalSessionsLastYear={dashboardData.totalSessionsLastYear}
              longestStreakDays={dashboardData.longestStreakDays}
              currentStreakDays={dashboardData.currentStreakDays}
              totalActiveDays={dashboardData.totalActiveDays}
            />
          </Box>
        </Box>
      </Container>
      </Box>
    </Box>
  )
}

export default Dashboard
